import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Paywall from "@/pages/Paywall";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Documents from "@/pages/Documents";
import NewDocument from "@/pages/NewDocument";
import DocumentEditor from "@/pages/DocumentEditor";
import DocumentWizard from "@/pages/DocumentWizard";
import CollectSignatures from "@/pages/CollectSignatures";
import EquipmentDetail from "@/pages/EquipmentDetail";
import Maintenance from "@/pages/Maintenance";
import Assistant from "@/pages/Assistant";
import Contracts from "@/pages/Contracts";
import Opportunities from "@/pages/Opportunities";
import DocumentView from "@/pages/DocumentView";
import PublicSign from "@/pages/PublicSign";
import ResetPassword from "@/pages/ResetPassword";
import Plans from "@/pages/Plans";
import Payments from "@/pages/Payments";
import Reminders from "@/pages/Reminders";
import Recibos from "@/pages/Recibos";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
  </div>
);

function AppShell() {
  const { user, loading } = useAuth();
  const [company, setCompany] = React.useState(undefined);
  const [sub, setSub] = React.useState(undefined);

  React.useEffect(() => {
    if (user) {
      api.get("/company").then(({ data }) => setCompany(data && data.company_id ? data : null)).catch(() => setCompany(null));
      api.get("/subscription").then(({ data }) => setSub(data)).catch(() => setSub(null));
    }
  }, [user]);

  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (company === undefined || sub === undefined) return <Spinner />;
  if (sub?.blocked && !user?.is_admin) return <Paywall onActivated={() => window.location.reload()} />;
  if (!company && !user?.is_admin && window.location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;

  return <Layout company={company} />;
}

function ProtectedRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/documents/new" element={<NewDocument />} />
        <Route path="/documents/new/:type" element={<DocumentWizard />} />
        <Route path="/documents/:id/edit" element={<DocumentWizard />} />
        <Route path="/documents/:id/edit-classic" element={<DocumentEditor />} />
        <Route path="/documents/:id/sign" element={<CollectSignatures />} />
        <Route path="/equipments/:id" element={<EquipmentDetail />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/crm" element={<Opportunities />} />
        <Route path="/documents/:id" element={<DocumentView />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/recibos" element={<Recibos />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={loading ? <Spinner /> : user ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/login" element={loading ? <Spinner /> : user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/doc/:token" element={<PublicSign />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/*" element={<ProtectedRoutes />} />
    </Routes>
  );
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
